// Memory only. Every completed reuse still asks the API to check access and ETag.
const MAX_READ_BYTES = 512 * 1024;
const MAX_BYTES = 16 * 1024 * 1024;
const MAX_ENTRIES = 64;
const LIFETIME_MS = 30000;
const entries = new Map();
const pending = new Map();
let bytes = 0;
let session;
let generation = 0;

const abortError = () => new DOMException('Asset read cancelled', 'AbortError');
const remove = key => {
  const entry = entries.get(key);
  if (entry) bytes -= entry.data.byteLength;
  entries.delete(key);
};

export const clearJBrowseAssetCache = () => {
  generation += 1;
  entries.clear();
  bytes = 0;
  for (const read of pending.values()) read.controller.abort();
  pending.clear();
};

const retain = (key, entry) => {
  remove(key);
  entries.set(key, entry);
  bytes += entry.data.byteLength;
  while (bytes > MAX_BYTES || entries.size > MAX_ENTRIES) remove(entries.keys().next().value);
};

const responseFrom = entry => new Response(entry.data.slice(0), entry);

async function readAsset(input, init, key, currentGeneration) {
  const saved = entries.get(key);
  const headers = new Headers(init.headers);
  if (saved) headers.set('If-None-Match', saved.headers.get('ETag'));
  const response = await fetch(input, { ...init, headers });
  if (generation !== currentGeneration || localStorage.getItem('auth_token') !== session)
    throw abortError();
  if (response.status === 304 && saved) {
    // Do not extend the lifetime: periodically discard both bytes and validator.
    return { entry: saved };
  }
  remove(key);
  const length = Number(response.headers.get('Content-Length'));
  if (
    [200, 206].includes(response.status) &&
    response.headers.get('X-Oiko-Byte-Cache') === 'revalidate' &&
    response.headers.get('ETag') &&
    response.headers.has('Content-Length') &&
    Number.isSafeInteger(length) &&
    length >= 0 &&
    length <= MAX_READ_BYTES
  ) {
    const data = await response.arrayBuffer();
    if (generation !== currentGeneration || localStorage.getItem('auth_token') !== session)
      throw abortError();
    const entry = {
      data,
      status: response.status,
      statusText: response.statusText,
      headers: new Headers(response.headers),
      expires: Date.now() + LIFETIME_MS,
    };
    if (data.byteLength === length) retain(key, entry);
    return { entry };
  }
  return { response };
}

// A subscriber can cancel without cancelling another reader of the same block.
function subscribe(read, signal, fallback) {
  return new Promise((resolve, reject) => {
    read.users += 1;
    let finished = false;
    const finish = () => {
      if (finished) return false;
      finished = true;
      signal?.removeEventListener('abort', cancel);
      read.users -= 1;
      return true;
    };
    const cancel = () => {
      if (!finish()) return;
      if (!read.users) read.controller.abort();
      reject(abortError());
    };
    signal?.addEventListener('abort', cancel, { once: true });
    if (signal?.aborted) cancel();
    read.promise.then(
      result => {
        if (!finish()) return;
        if (result.entry) resolve(responseFrom(result.entry));
        else if (result.response) {
          const response = result.response;
          result.response = null;
          resolve(response);
        } else resolve(fallback()); // Unexpected large/legacy response: never buffer or tee it.
      },
      error => {
        if (finish()) reject(error);
      }
    );
  });
}

export function fetchCachedJBrowseAsset(input, init, token) {
  if (session !== token) {
    clearJBrowseAssetCache();
    session = token;
  }
  for (const [key, entry] of entries) if (entry.expires <= Date.now()) remove(key);
  const headers = new Headers(init.headers);
  if (init.signal?.aborted) return Promise.reject(abortError());
  if (headers.has('If-None-Match') || headers.has('If-Range')) return fetch(input, init);
  const key = JSON.stringify([
    new URL(input?.url || input, window.location.href).href,
    [...headers],
  ]);
  const range = /^bytes=(\d+)-(\d+)$/.exec(headers.get('Range') || '');
  const bounded =
    range &&
    Number(range[2]) >= Number(range[1]) &&
    Number(range[2]) - Number(range[1]) < MAX_READ_BYTES;
  if (!bounded)
    return readAsset(input, init, key, generation).then(result =>
      result.entry ? responseFrom(result.entry) : result.response
    );
  let read = pending.get(key);
  if (!read || read.controller.signal.aborted) {
    read = { controller: new AbortController(), users: 0 };
    read.promise = readAsset(
      input,
      { ...init, signal: read.controller.signal },
      key,
      generation
    ).finally(() => {
      if (pending.get(key) === read) pending.delete(key);
    });
    pending.set(key, read);
  }
  return subscribe(read, init.signal, () => fetch(input, init));
}
